import { ApprovalsService } from 'api-clients/monolith';

import {
  ConditionDraft,
  draftConditions,
  FieldCatalog,
  passthroughConditions,
  StoredCondition,
  toStoredConditions,
  validateCondition,
} from './conditionForm';

import {
  ApprovalRule,
  ApprovalRuleApprover,
  ApprovalRuleLevel,
  SubjectType,
} from './types';

/**
 * The write shape, taken from the generated client rather than described again
 * here — so a contract change surfaces as a type error in `toPayload` instead of
 * as a 422 at runtime.
 */
export type RulePayload = Parameters<
  typeof ApprovalsService.postInternalApiApprovalsRules
>[0];

/**
 * Conditions are `Array<any>` in the OpenAPI document on purpose: their shape
 * varies by condition type, and `Approvals::ConditionsValidator` is the
 * authority. This panel only carries them, so it matches that looseness rather
 * than inventing a stricter type it cannot enforce.
 */
type ConditionRows = NonNullable<RulePayload['rule']['scope_conditions']>;

/** One level's approver rows, as the write endpoint expects them. */
type ApproverRows = NonNullable<
  NonNullable<RulePayload['rule']['levels']>[number]['approvers']
>;

/**
 * The rule as the panel edits it.
 *
 * Deliberately not the API shape. `conditions`, `condition_logic` and the
 * escalation fields are carried on each level untouched — see `toPayload` for
 * why that matters — while the panel only ever writes `name`, `enabled` and
 * `approvers`.
 */
export interface LevelDraft {
  /** Stable within one editing session so React keys survive a reorder. */
  key: string;
  name: string;
  enabled: boolean;
  approverIds: string[];
  /**
   * User approvers the API returned without a `user_id`, i.e. whose user has
   * since been offboarded. They cannot be round-tripped and are dropped on save
   * — see `draftFor`. Counted so the panel can say so out loud rather than
   * quietly shrinking the level.
   *
   * Counts **primary and backup** rows. A dropped backup is the same loss of
   * coverage as a dropped primary, and the panel cannot show the backup roster
   * at all, so leaving them out would make the warning silent for the one case
   * the admin has no other way to see.
   */
  offboardedApproverCount: number;
  /** The data-key conditions this level is gated on (AXHE-3929). */
  conditions: ConditionDraft[];
  /** Condition types this builder does not render, carried through untouched. */
  conditionPassthrough: StoredCondition[];
  /**
   * The level's conditions exactly as the API returned them.
   *
   * Kept so a save with no catalog re-emits what was there rather than a set
   * rebuilt by a builder that could not read it. Without the catalog the builder
   * is not rendered and these rows cannot have been edited, so replaying them is
   * the definition of leaving them alone — not a coincidence of
   * `toStoredConditions` happening to round-trip.
   */
  storedConditions: StoredCondition[];
  /** Round-tripped, never edited here. */
  passthrough: Omit<
    ApprovalRuleLevel,
    'name' | 'enabled' | 'approvers' | 'position' | 'conditions'
  >;
}

export interface RuleDraft {
  name: string;
  subjectType: SubjectType;
  enabled: boolean;
  levels: LevelDraft[];
  /** Round-tripped, never edited here. */
  scopeConditions: ConditionRows;
  scopeConditionLogic: 'and' | 'or';
}

export const emptyLevel = (key: string): LevelDraft => ({
  key,
  name: '',
  enabled: true,
  approverIds: [],
  offboardedApproverCount: 0,
  conditions: [],
  conditionPassthrough: [],
  storedConditions: [],
  passthrough: {},
});

export const draftForNew = (subjectType: SubjectType): RuleDraft => ({
  name: '',
  subjectType,
  // A new rule starts active: an admin who just wrote a gate meant it to gate.
  enabled: true,
  levels: [emptyLevel('level-0')],
  scopeConditions: [],
  scopeConditionLogic: 'and',
});

const isRoleApprover = (approver: ApprovalRuleApprover) =>
  approver.approver_type === 'role';

/**
 * A role approver narrowed from the read shape to the write shape.
 *
 * The read type widens `approver_type` to string and makes the role fields
 * nullable; carrying those straight back would not satisfy the write contract.
 */
const roleRow = (approver: ApprovalRuleApprover) => ({
  approver_type: 'role' as const,
  role_name: approver.role_name ?? undefined,
  role_scope: approver.role_scope ?? undefined,
});

/**
 * A *user* approver with no user_id is one whose user has been offboarded:
 * `RuleLevelApprover#user` is scoped to live users, so the serializer emits
 * `user_id: null` for them. They are genuinely unsendable — the write contract
 * requires a user_id and `SaveRule#user_for` resolves it through
 * `account.users`, which excludes deleted users. That matches the engine, which
 * already resolves them to nobody (`ApproverResolver#resolve_user` returns []
 * for a nil user). The defect was only that it happened invisibly, so they are
 * counted and the panel says so.
 */
const offboardedCount = (approvers: ApprovalRuleApprover[] = []) =>
  approvers.filter(approver => !isRoleApprover(approver) && !approver.user_id).length;

/**
 * An approver list reduced to the rows the write endpoint will accept.
 *
 * Dropping the offboarded rows is not cosmetic. Sent back as
 * `{ approver_type: 'user', user_id: null }` the server does not skip them:
 * `SaveRule#user_for` returns nil for a blank user_id, and
 * `RuleLevelApprover#identity_matches_approver_type` then fails the row with
 * "User must be set for a user approver" — which fails the *whole* save with a
 * 422. Filtering here is what keeps a rule editable after one of its approvers
 * has left.
 */
const sendableApprovers = (approvers: ApprovalRuleApprover[] = []): ApproverRows =>
  approvers
    .filter(approver => isRoleApprover(approver) || approver.user_id)
    .map(approver =>
      isRoleApprover(approver)
        ? roleRow(approver)
        : { approver_type: 'user' as const, user_id: approver.user_id as string },
    );

export const draftFor = (rule: ApprovalRule): RuleDraft => ({
  name: rule.name ?? '',
  subjectType: rule.subject_type ?? 'OfferLetter',
  enabled: rule.enabled ?? true,
  scopeConditions: rule.scope_conditions ?? [],
  scopeConditionLogic: rule.scope_condition_logic ?? 'and',
  levels: (rule.levels ?? []).map((level, index) => {
    // `backup_approvers` is pulled out of the passthrough rest on purpose. The
    // panel does not edit backups — they only apply once the escalation timer
    // expires — so they were previously round-tripped verbatim, which sent an
    // offboarded backup straight back as `user_id: null` and made the rule
    // unsavable from a panel that cannot even show the row. They are
    // round-tripped still, but through the same filter and narrowing the
    // primary rows go through.
    //
    // `conditions` is pulled out for the same reason in reverse: this builder
    // *does* edit them, so they must not be spread back verbatim from
    // `passthrough` on top of the edited set.
    const {
      name,
      enabled,
      approvers,
      backup_approvers: backups,
      position,
      conditions,
      ...passthrough
    } = level;
    const stored = (conditions ?? []) as StoredCondition[];

    return {
      // Keyed by array index rather than `position`. Both survive a reorder
      // equally — a reorder rewrites the array and the keys are assigned once
      // per draft — but `position ?? index` can collide if one level carries a
      // position and its neighbour does not (level 0 with a null position and
      // level 1 with position 0 both become `level-0`), and two levels sharing a
      // key would share a validation message and a form field id.
      key: `level-${index}`,
      conditions: draftConditions(stored),
      conditionPassthrough: passthroughConditions(stored),
      storedConditions: stored,
      name: name ?? '',
      enabled: enabled ?? true,
      // Role approvers have no user_id. They are round-tripped through
      // `passthrough` rather than being dropped, so editing a rule that names a
      // role does not quietly delete that role from the level.
      approverIds: (approvers ?? [])
        .filter(approver => !isRoleApprover(approver) && approver.user_id)
        .map(approver => approver.user_id as string),
      offboardedApproverCount: offboardedCount(approvers) + offboardedCount(backups),
      passthrough: {
        ...passthrough,
        // Only re-added when the level actually carried the key, so a level the
        // API did not describe backups for still submits without it.
        ...(backups && { backup_approvers: sendableApprovers(backups) }),
        // Narrowed to the *write* shape here rather than at submit, so the draft
        // holds only things it can actually send.
        roleApprovers: (approvers ?? []).filter(isRoleApprover).map(roleRow),
      } as LevelDraft['passthrough'],
    };
  }),
});

export type LevelError = 'no_approvers' | 'bad_condition';

export interface ValidationErrors {
  name?: 'required';
  /**
   * Keyed by level key, so the message lands on the level that is wrong, and
   * carrying **every** problem on that level rather than the first.
   *
   * A level with no approvers and a half-written condition has two things wrong
   * with it. Reporting one, then the other after the admin fixes it and saves
   * again, is a round trip per mistake — the same argument `ValidatePlacements`
   * already states for a form that submits its whole set in one request.
   */
  levels: Record<string, LevelError[]>;
}

/**
 * @param catalog the loaded field catalog, or `undefined` when it has not
 *   arrived. Without it the condition builder is not rendered, so a condition
 *   cannot have been edited and there is nothing here to judge it against —
 *   validating anyway would flag every stored condition as unknown and block a
 *   save that only touched approvers.
 */
export const validate = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): ValidationErrors => {
  const levels: ValidationErrors['levels'] = {};

  draft.levels.forEach(level => {
    const roleApprovers =
      (level.passthrough as { roleApprovers?: ApproverRows }).roleApprovers ?? [];
    const problems: LevelError[] = [];

    // A level is staffed by named users OR by roles resolved at submission. Only
    // counting users would report a role-only level as empty and refuse to save
    // a rule the server considers perfectly valid.
    if (level.approverIds.length === 0 && roleApprovers.length === 0) {
      problems.push('no_approvers');
    }

    // A half-written condition would be saved as one the engine cannot
    // evaluate, which holds every offer for a human rather than gating the ones
    // the admin meant.
    if (
      catalog &&
      level.conditions.some(condition => validateCondition(condition, catalog))
    ) {
      problems.push('bad_condition');
    }

    if (problems.length > 0) levels[level.key] = problems;
  });

  return {
    name: draft.name.trim() ? undefined : 'required',
    levels,
  };
};

/** Whether a level was flagged for this specific problem. */
export const levelHasError = (
  errors: ValidationErrors,
  levelKey: string,
  error: LevelError,
) => (errors.levels[levelKey] ?? []).includes(error);

export const hasErrors = (errors: ValidationErrors) =>
  errors.name !== undefined || Object.keys(errors.levels).length > 0;

/**
 * The draft as the API wants it.
 *
 * ## Why every level carries its conditions back
 *
 * PATCH replaces the level tree wholesale — "Sent, it replaces the whole tree —
 * a level absent from the array is deleted" — and the controller reads each
 * level's conditions straight out of the submitted document
 * (`condition_rows(raw_level[:conditions])`), where a missing key becomes `[]`.
 *
 * So a panel that submitted levels without their conditions would not leave them
 * alone: it would silently **clear** them, turning a rule that gates offers over
 * $100k into one that gates every offer. Round-tripping them is what makes it
 * safe for this panel to edit names and approvers without owning the condition
 * builder (AXHE-4107).
 *
 * The same argument applies to `scope_conditions` at the rule level.
 *
 * @param catalog the loaded field catalog, or `undefined`. Without it the
 *   builder never rendered, so each level's conditions are re-emitted exactly as
 *   the API returned them rather than rebuilt from drafts read without the
 *   vocabulary needed to interpret them.
 */
export const toPayload = (draft: RuleDraft, catalog?: FieldCatalog): RulePayload => ({
  rule: {
    name: draft.name.trim(),
    subject_type: draft.subjectType,
    enabled: draft.enabled,
    scope_conditions: draft.scopeConditions,
    scope_condition_logic: draft.scopeConditionLogic,
    levels: draft.levels.map(level => {
      const { roleApprovers = [], ...otherFields } =
        level.passthrough as { roleApprovers?: ApproverRows };

      return {
        ...otherFields,
        // Edited rows plus the condition types this builder does not render, so
        // editing a data-key condition cannot delete a rule's position or
        // location scoping along with it.
        conditions: catalog
          ? toStoredConditions(level.conditions, level.conditionPassthrough, catalog)
          : level.storedConditions,
        // `undefined`, not `null`: a level's name is nullable when read back but
        // the write contract only accepts a string or its absence, and an
        // unnamed level is one the payload simply does not name.
        name: level.name.trim() || undefined,
        enabled: level.enabled,
        // Position is deliberately absent: the server assigns it from this
        // array's order, so the client cannot desync the numbering from the
        // sequence it is displaying.
        approvers: [
          ...level.approverIds.map(userId => ({
            approver_type: 'user' as const,
            user_id: userId,
          })),
          ...roleApprovers,
        ],
      };
    }),
  },
});
